import { useEffect } from "react";
import { useTenant } from "@/contexts/TenantContext";

interface SEOProps {
  /**
   * Page title suffix (will be combined with tenant name)
   * Example: "Search" becomes "Pegasos - Search"
   */
  title?: string;
  /**
   * Meta description for the page
   */
  description?: string;
  /**
   * Override the default og:image (tenant logo)
   */
  image?: string;
  /**
   * Canonical URL path (without domain)
   */
  canonicalPath?: string;
  /**
   * Page type for Open Graph
   */
  type?: "website" | "article";
  /**
   * Prevent indexing of this page
   */
  noindex?: boolean;
}

/**
 * SEO component for managing page meta tags.
 * Uses document.head manipulation for React 18 compatibility.
 *
 * Features:
 * - Dynamic page titles: {Tenant} - {Page}
 * - Meta descriptions per page
 * - Open Graph tags for social sharing
 * - Twitter Card tags
 * - Canonical URLs
 * - Robots meta for noindex pages
 */
export function SEO({
  title,
  description,
  image,
  canonicalPath,
  type = "website",
  noindex = false,
}: SEOProps) {
  const { tenant, slug } = useTenant();

  useEffect(() => {
    if (!tenant) return;

    // Build page title
    const pageTitle = title ? `${tenant.name} - ${title}` : tenant.name;
    document.title = pageTitle;

    // Get current URL for canonical and og:url
    const baseUrl = window.location.origin;
    const currentPath = canonicalPath || window.location.pathname;
    const canonicalUrl = `${baseUrl}${currentPath}`;

    // Get tenant logo for og:image
    const ogImage = image || tenant.publicLogoUrl || `${baseUrl}/og-default.png`;

    // Default description
    const metaDescription =
      description ||
      `Book bus tickets with ${tenant.name}. Fast, easy online reservations.`;

    // Update or create meta tags
    updateMetaTag("description", metaDescription);
    updateMetaTag("robots", noindex ? "noindex, nofollow" : "index, follow");

    // Open Graph tags
    updateMetaTag("og:title", pageTitle, "property");
    updateMetaTag("og:description", metaDescription, "property");
    updateMetaTag("og:image", ogImage, "property");
    updateMetaTag("og:url", canonicalUrl, "property");
    updateMetaTag("og:type", type, "property");
    updateMetaTag("og:site_name", tenant.name, "property");

    // Twitter Card tags
    updateMetaTag("twitter:card", "summary_large_image", "name");
    updateMetaTag("twitter:title", pageTitle, "name");
    updateMetaTag("twitter:description", metaDescription, "name");
    updateMetaTag("twitter:image", ogImage, "name");

    // Canonical URL
    updateLinkTag("canonical", canonicalUrl);

    // Cleanup function to reset title on unmount
    return () => {
      // Optionally reset title, but usually not needed as next page will set it
    };
  }, [tenant, slug, title, description, image, canonicalPath, type, noindex]);

  return null; // This component doesn't render anything
}

/**
 * Update or create a meta tag
 */
function updateMetaTag(
  name: string,
  content: string,
  attrName: "name" | "property" = "name"
): void {
  let meta = document.querySelector(
    `meta[${attrName}="${name}"]`
  ) as HTMLMetaElement | null;

  if (!meta) {
    meta = document.createElement("meta");
    meta.setAttribute(attrName, name);
    document.head.appendChild(meta);
  }

  meta.content = content;
}

/**
 * Update or create a link tag
 */
function updateLinkTag(rel: string, href: string): void {
  let link = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;

  if (!link) {
    link = document.createElement("link");
    link.rel = rel;
    document.head.appendChild(link);
  }

  link.href = href;
}

// Export page-specific SEO presets
export const SEOPresets = {
  search: {
    title: "Search Trips",
    description: "Search and book bus tickets. Choose your route, date, and seats.",
  },
  tripResults: {
    title: "Available Trips",
    description: "View available trips and departure times. Select your preferred trip.",
  },
  seatSelection: {
    title: "Select Seats",
    description: "Choose your seats on the bus. Interactive seat map with real-time availability.",
  },
  checkout: {
    title: "Checkout",
    description: "Enter passenger details and complete your booking.",
  },
  payment: {
    title: "Payment",
    description: "Secure payment for your bus tickets.",
    noindex: true, // Don't index payment pages
  },
  confirmation: {
    title: "Booking Confirmed",
    description: "Your booking is confirmed. View your ticket details and QR code.",
    noindex: true, // Don't index confirmation pages
  },
  ticket: {
    title: "Your Ticket",
    description: "View your bus ticket with QR code for boarding.",
    noindex: true, // Don't index individual tickets
  },
} as const;
